/* Header state A -> state B.
 *
 * Every value here is hardcoded on purpose. Section-group settings do not reliably sync
 * through the Shopify GitHub integration, so the theme editor could serve a stale or
 * schema-default value on the live theme; a scroll threshold that read empty would leave
 * the header stuck in one state. See CLAUDE.md rule 4.
 *
 * The sticky element is the Shopify-generated section wrapper, not `.dp-header` — a sticky
 * box only travels inside its own parent, and Shopify wraps each section in a div exactly
 * as tall as it. The wrapper is therefore what carries the state attribute, and the CSS in
 * `sections/dp-header.liquid` reads it.
 *
 * That wrapper is also the node the theme editor throws away when it re-renders the
 * header, and the editor does not re-execute `<script src>`. So the scroll listener is
 * registered once against `window` and the wrapper is looked up again on every
 * `shopify:section:load`: without that, editing any header setting left this file writing
 * to a detached node and the header never morphed again for the rest of the session.
 */
(function () {
  'use strict';

  if (window.dpHeaderMorphLoaded) return;
  window.dpHeaderMorphLoaded = true;

  var THRESHOLD = 140;

  /* HIDE ON THE WAY DOWN, SHOW ON THE WAY UP (client, 2026-09-23). `data-dp-header-hidden`
     is stamped while the reader is scrolling down past HIDE_AFTER and removed the moment
     they scroll back up or reach the top. Only the classic phone bar reads it
     (`.dp-header--classic`, every template but the TUDCA product page), sliding the sticky
     wrapper up by its own height; the pill bar there never hides, and desktop never did.
     SETTLE is the dead band that stops a finger's tremor flickering it. The bar never
     hides while the mobile menu is open or while anything inside it has focus, so
     keyboard and screen-reader users are never scrolled away from the control they are
     on. */
  var HIDE_AFTER = 80;
  var SETTLE = 6;

  /* OFF THE TOP (client, 2026-09-26). `data-dp-header-off-top` is stamped as soon as the
     page has left the very top — past OFF_TOP, a dead band of a few pixels so a
     rubber-band bounce cannot flicker it. The phone bar reads it: at the top one bare row,
     the wordmark large in the brand's dark green and "Shop now" folded away; off the top
     two frosted pills, and "Shop now" opens. The stylesheet only acts on it below 900px. */
  var OFF_TOP = 4;

  /* BUY AHEAD (client, 2026-09-28). Where the page shows an add-to-cart button — the
     purchase-options submit (`[data-dp-po-submit]`) or the older buy box's
     (`[data-dp-buy-submit]`), the first one actually rendered — `data-dp-header-buy-ahead`
     is stamped until the reader has scrolled past it, and the phone bar keeps "Shop now"
     folded while it is there. Past it (its bottom edge above the viewport, the same test
     dp-sticky-buy.js uses) the attribute goes and "Shop now" opens.

     Where no add button is rendered — none on the page, or every one hidden (a sold-out
     state, a buy section switched off on a template) — the attribute is never stamped and
     "Shop now" behaves as it always did: it opens as soon as the page leaves the top
     (client, 2026-09-28, "use the old header" on those pages). An unrendered button must
     not count: its rectangle is all zeros, and `bottom >= 0` would hold it "ahead"
     forever. Read from the rectangle each frame rather than an IntersectionObserver, for
     the same reason as dp-sticky-buy.js: a jump past the button must not leave a stale
     state. */
  var BUY_BUTTONS = '[data-dp-po-submit], [data-dp-buy-submit]';

  var wrapper = null;
  var scrolled = null;
  var hidden = null;
  var lastY = 0;
  var offTop = null;
  var buyAhead = null;
  var buyButton = null;

  function rendered(node) {
    return !!node && node.isConnected && node.getClientRects().length > 0;
  }

  function findBuyButton() {
    if (rendered(buyButton)) return buyButton;
    var buttons = document.querySelectorAll(BUY_BUTTONS);
    for (var i = 0; i < buttons.length; i++) {
      if (rendered(buttons[i])) return buttons[i];
    }
    return null;
  }

  function menuOpen() {
    return !!document.querySelector('.dp-mobile-menu[data-dp-open="true"]');
  }

  function stamp(name, on) {
    if (on) {
      wrapper.setAttribute(name, 'true');
    } else {
      wrapper.removeAttribute(name);
    }
  }

  function apply() {
    if (!wrapper) return;

    var y = window.scrollY || document.documentElement.scrollTop || 0;

    var nextScrolled = y > THRESHOLD;
    if (nextScrolled !== scrolled) {
      scrolled = nextScrolled;
      stamp('data-dp-header-scrolled', nextScrolled);
    }

    var nextOffTop = y > OFF_TOP;
    if (nextOffTop !== offTop) {
      offTop = nextOffTop;
      stamp('data-dp-header-off-top', nextOffTop);
    }

    buyButton = findBuyButton();
    var nextBuyAhead = !!buyButton && buyButton.getBoundingClientRect().bottom >= 0;
    if (nextBuyAhead !== buyAhead) {
      buyAhead = nextBuyAhead;
      stamp('data-dp-header-buy-ahead', nextBuyAhead);
    }

    var delta = y - lastY;
    var nextHidden = hidden;
    if (y <= HIDE_AFTER || menuOpen() || wrapper.contains(document.activeElement)) {
      nextHidden = false;
    } else if (delta > SETTLE) {
      nextHidden = true;
    } else if (delta < -SETTLE) {
      nextHidden = false;
    }
    if (Math.abs(delta) > SETTLE || y <= HIDE_AFTER) lastY = y;
    if (nextHidden !== hidden) {
      hidden = nextHidden;
      stamp('data-dp-header-hidden', nextHidden);
    }
  }

  /* One read per frame, however many scroll events the browser fires. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      apply();
    });
  }

  function resolve() {
    var header = document.querySelector('.dp-header');
    var next = null;

    if (header) {
      next = header.closest('.shopify-section-group-header-group') || header.parentElement;
    }
    if (next === wrapper) return;

    wrapper = next;
    /* The replacement wrapper arrives without the attributes, so the cached state has to
       be forgotten or `apply` would short-circuit and leave them unstamped. */
    scrolled = null;
    hidden = null;
    offTop = null;
    buyAhead = null;
    buyButton = null;
    apply();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolve);
  } else {
    resolve();
  }

  document.addEventListener('shopify:section:load', resolve);
})();
